import { ComponentBase } from "../../core/ComponentBase.js";

export class WorkspaceChatInput extends ComponentBase {
    static styles = `
        /* ── Floating input shell ── */
        .ws-input-shell {
            /* No background, no border — pure container */
            padding: 0 1rem 1.25rem;
            display: flex;
            justify-content: center;
            /* iPhone home bar safe area */
            padding-bottom: max(1.25rem, env(safe-area-inset-bottom));
        }
        @media (max-width: 768px) {
            .ws-input-shell {
                padding-left: 0.5rem;
                padding-right: 0.5rem;
            }
        }

        .ws-input-float {
            width: 100%;
            max-width: 760px;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        /* Attachment pill row */
        .ws-attach-preview {
            display: flex;
            flex-wrap: wrap;
            gap: 0.4rem;
            padding: 0 0.25rem;
        }
        .ws-followup-preview {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            padding: 0 0.25rem;
        }
        .ws-followup-pill {
            display: flex;
            align-items: center;
            gap: 0.45rem;
            min-height: 30px;
            padding: 0.35rem 0.65rem;
            border-radius: var(--ab-radius-sm);
            font-size: var(--ab-text-xs);
            background: var(--bs-secondary-bg);
            border: 1px solid var(--bs-border-color-translucent);
            color: var(--bs-body-color);
        }
        .ws-followup-pill .followup-text {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
        }
        .ws-followup-pill .remove-followup {
            border: 0;
            background: transparent;
            color: var(--bs-secondary-color);
            width: 24px;
            height: 24px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: var(--ab-radius-full);
            transition: background-color var(--ab-t-fast), color var(--ab-t-fast);
        }
        @media (hover: hover) {
            .ws-followup-pill .remove-followup:hover {
                background-color: var(--ab-hover-bg);
                color: var(--bs-body-color);
            }
        }
        .ws-followup-pill .remove-followup:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }
        .ws-attach-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            padding: 0.3rem 0.65rem;
            border-radius: var(--ab-radius-pill);
            font-size: var(--ab-text-xs);
            font-weight: var(--ab-weight-medium);
            background: var(--bs-secondary-bg);
            border: 1px solid var(--bs-border-color-translucent);
            color: var(--bs-body-color);
            max-width: 200px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .ws-attach-pill .remove-att {
            cursor: pointer;
            opacity: 0.5;
            flex-shrink: 0;
            transition: opacity var(--ab-t-fast);
        }
        .ws-attach-pill .remove-att:hover { opacity: 1; }

        /* The main input card */
        .ws-input-card {
            background: var(--bs-tertiary-bg);
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--ab-radius-lg);
            box-shadow: var(--ab-shadow-sm);
            transition: border-color var(--ab-t-base), box-shadow var(--ab-t-base);
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }
        .ws-input-card:focus-within {
            border-color: var(--ab-focus-border);
            box-shadow: var(--ab-focus-ring), var(--ab-shadow-md);
        }

        /* Textarea itself */
        .ws-textarea {
            width: 100%;
            border: none;
            background: transparent;
            resize: none;
            padding: 0.95rem 1.1rem 0.35rem;
            outline: none;
            color: var(--bs-body-color);
            font-size: var(--ab-text-base);
            line-height: 1.55;
            min-height: 52px;
            max-height: 220px;
            scrollbar-width: thin;
            overflow-y: auto;
            caret-color: var(--bs-primary);
        }
        .ws-textarea::placeholder { color: var(--bs-secondary-color); opacity: 0.75; }

        /* Bottom row of the card: left tools + right send */
        .ws-input-actions {
            display: flex;
            align-items: center;
            padding: 0.35rem 0.5rem 0.5rem 0.6rem;
            gap: 0.25rem;
        }
        .ws-input-actions-right {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 0.4rem;
        }

        /* Ghost icon buttons (attach, voice) */
        .ws-tool-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 34px;
            height: 34px;
            border-radius: var(--ab-radius-full);
            border: none;
            background: transparent;
            color: var(--bs-secondary-color);
            font-size: var(--ab-text-md);
            cursor: pointer;
            transition: background-color var(--ab-t-fast), color var(--ab-t-fast);
        }
        @media (hover: hover) {
            .ws-tool-btn:hover {
                background-color: var(--ab-hover-bg);
                color: var(--bs-body-color);
            }
        }
        .ws-tool-btn:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }
        .ws-tool-btn.active {
            color: var(--ab-active-color);
            background-color: var(--ab-active-bg);
        }

        /* Send / Stop button */
        .ws-send-btn, .ws-stop-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border-radius: var(--ab-radius-full);
            border: none;
            background: var(--bs-primary);
            color: var(--bs-primary-contrast, #fff);
            font-size: var(--ab-text-sm);
            cursor: pointer;
            transition: transform var(--ab-t-fast), background-color var(--ab-t-fast), opacity var(--ab-t-fast);
            flex-shrink: 0;
        }
        @media (hover: hover) {
            .ws-send-btn:hover:not(:disabled) {
                background-color: color-mix(in srgb, var(--bs-primary), black 15%);
                transform: var(--ab-lift-sm);
            }
        }
        .ws-send-btn:focus-visible, .ws-stop-btn:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }
        .ws-send-btn:disabled {
            opacity: 0.35;
            cursor: not-allowed;
            transform: none !important;
        }
        .ws-send-btn.is-stop {
            background-color: var(--bs-body-color);
            color: var(--bs-body-bg);
        }
        @media (hover: hover) {
            .ws-send-btn.is-stop:hover:not(:disabled) {
                background-color: color-mix(in srgb, var(--bs-body-color), white 15%);
            }
        }
        .ws-stop-btn {
            display: none;
            background-color: var(--bs-danger);
            color: var(--bs-danger-contrast, #fff);
        }
        .ws-stop-btn.visible {
            display: inline-flex;
        }
        @media (hover: hover) {
            .ws-stop-btn:hover {
                background-color: color-mix(in srgb, var(--bs-danger), black 15%);
                transform: var(--ab-lift-sm);
            }
        }
        .ws-followup-actions {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            padding: 0 0.25rem;
        }

        /* hint text below */
        /* (thinking animation is now rendered in the chat area, not here) */
        .ws-input-hint {
            text-align: center;
            font-size: var(--ab-text-2xs);
            color: var(--bs-secondary-color);
            opacity: 0.55;
            padding: 0 0.5rem;
        }
    `;

    constructor(config) {
        super(config);
        this.workspace = config.workspace;
        this.isProcessing = false;
        this.fileUploadComponent = null;
        this.attachments = [];
        this.applyStyles();
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'ws-input-shell';

            const floatWrap = document.createElement('div');
            floatWrap.className = 'ws-input-float';

            // Attachment previews
            this.previewArea = document.createElement('div');
            this.previewArea.className = 'ws-attach-preview';
            floatWrap.appendChild(this.previewArea);

            this.followUpArea = document.createElement('div');
            this.followUpArea.className = 'ws-followup-preview';
            floatWrap.appendChild(this.followUpArea);

            // Card
            const card = document.createElement('div');
            card.className = 'ws-input-card';

            // Textarea
            this.textarea = document.createElement('textarea');
            this.textarea.className = 'ws-textarea';
            this.textarea.placeholder = 'Ask anything…';
            this.textarea.rows = 1;
            card.appendChild(this.textarea);

            // Actions row
            const actions = document.createElement('div');
            actions.className = 'ws-input-actions';

            // Left: attach
            this.attachBtn = document.createElement('button');
            this.attachBtn.className = 'ws-tool-btn';
            this.attachBtn.title = 'Attach file';
            this.attachBtn.innerHTML = '<i class="bi bi-paperclip"></i>';
            actions.appendChild(this.attachBtn);

            // Right: voice + send
            const right = document.createElement('div');
            right.className = 'ws-input-actions-right';

            this.voiceBtn = document.createElement('button');
            this.voiceBtn.className = 'ws-tool-btn';
            this.voiceBtn.title = 'Voice mode';
            this.voiceBtn.innerHTML = '<i class="bi bi-mic"></i>';
            right.appendChild(this.voiceBtn);

            this.stopBtn = document.createElement('button');
            this.stopBtn.className = 'ws-stop-btn';
            this.stopBtn.title = 'Stop response';
            this.stopBtn.innerHTML = '<i class="bi bi-square-fill"></i>';
            right.appendChild(this.stopBtn);

            this.sendBtn = document.createElement('button');
            this.sendBtn.className = 'ws-send-btn';
            this.sendBtn.disabled = true;
            this.sendBtn.innerHTML = '<i class="bi bi-arrow-up"></i>';
            right.appendChild(this.sendBtn);

            actions.appendChild(right);
            card.appendChild(actions);
            floatWrap.appendChild(card);

            // Hint
            const hint = document.createElement('div');
            hint.className = 'ws-input-hint';
            hint.textContent = 'Enter to send · Shift+Enter for new line';
            floatWrap.appendChild(hint);

            this.container.appendChild(floatWrap);
            this._attachEvents();
            this.updateCapabilities();
        }
        return this.container;
    }

    _attachEvents() {
        // Auto-grow textarea
        this.textarea.addEventListener('input', () => {
            this.textarea.style.height = 'auto';
            this.textarea.style.height = Math.min(this.textarea.scrollHeight, 220) + 'px';
            this._updateSendBtn();
        });

        this.textarea.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this._submit();
            }
        });

        this.sendBtn.addEventListener('click', () => {
            this._submit();
        });

        this.stopBtn.addEventListener('click', () => {
            this.workspace.cancelGeneration();
        });

        this.attachBtn.addEventListener('click', async () => {
            if (!this.fileUploadComponent) {
                this.fileUploadComponent = await this.createManagedComponent('FileUpload', this.container, {
                    instanceId: `${this.config.instanceId}-uploader`,
                    hidden: true,
                    maxFiles: 20,
                    maxFileSize: 100 * 1024 * 1024, // 100MB
                    getUploadKey: async () => {
                        if (!(await this.workspace.ensureThread())) throw new Error('A conversation is required for uploads.');
                        return this.appClient.rpcClient.call('AiRpc.getAttachmentUploadKey', {
                            agentName: this.workspace.currentAgentName,
                            threadId: this.workspace.currentThreadId
                        }, { projectId: this.workspace.currentProjectId });
                    }
                });


                this.fileUploadComponent.on('uploadStarted', () => {
                    this.attachBtn.disabled = true;
                    this.attachBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" style="width: 14px; height: 14px;"></span>';
                });

                this.fileUploadComponent.on('uploadFailed', (err) => {
                    this.attachBtn.disabled = false;
                    this.attachBtn.innerHTML = '<i class="bi bi-paperclip"></i>';
                    this.appClient.notifier.error('Failed to upload file.');
                    this.fileUploadComponent.clear();
                });

                this.fileUploadComponent.on('uploadCompleted', async () => {
                    const uploadKey = this.fileUploadComponent.getValue();
                    if (!uploadKey) {
                        this.attachBtn.disabled = false;
                        this.attachBtn.innerHTML = '<i class="bi bi-paperclip"></i>';
                        return;
                    }
                    try {
                        const res = await this.appClient.rpcClient.call('AiRpc.finalizeAttachments', {
                            agentName: this.workspace.currentAgentName,
                            uploadKey
                        }, { projectId: this.workspace.currentProjectId });

                        if (res.success && res.files) {
                            res.files.forEach(f => this.attachments.push({
                                fileId: f.filePath,
                                fileName: f.fileName || 'file',
                                mimeType: f.fileType || 'application/octet-stream',
                                s3Uri: f.s3Uri
                            }));
                            this._renderPreviews();
                            this._updateSendBtn();
                        }
                    } catch (e) {
                        this.appClient.notifier.error('Failed to attach files.');
                    } finally {
                        this.fileUploadComponent.clear();
                        this.attachBtn.disabled = false;
                        this.attachBtn.innerHTML = '<i class="bi bi-paperclip"></i>';
                    }
                });
            }
            this.fileUploadComponent.openFile();
        });

        // Voice button delegates to chat area's startVoiceMode
        this.voiceBtn.addEventListener('click', () => {
            const chatArea = this.workspace.chatAreaComponent;
            if (chatArea && typeof chatArea.startVoiceMode === 'function') {
                chatArea.startVoiceMode();
            }
        });
    }

    _renderPreviews() {
        this.previewArea.innerHTML = '';
        this.attachments.forEach((att, i) => {
            const pill = document.createElement('span');
            pill.className = 'ws-attach-pill';
            pill.innerHTML = `
                <i class="bi bi-file-earmark"></i>
                <span>${att.fileName}</span>
                <i class="bi bi-x remove-att" data-idx="${i}"></i>
            `;
            pill.querySelector('.remove-att').addEventListener('click', () => {
                this.attachments.splice(i, 1);
                this._renderPreviews();
                this._updateSendBtn();
            });
            this.previewArea.appendChild(pill);
        });
    }

    _updateSendBtn() {
        const hasContent = this.textarea.value.trim().length > 0 || this.attachments.length > 0;
        this.sendBtn.disabled = !hasContent;
    }

    setProcessingState(isProcessing) {
        this.isProcessing = isProcessing;
        if (isProcessing) {
            this.sendBtn.className = 'ws-send-btn';
            this.sendBtn.innerHTML = '<i class="bi bi-arrow-up"></i>';
            if (this.stopBtn) this.stopBtn.classList.add('visible');
            this.textarea.disabled = false; // keep typing enabled — AI thinking doesn't block input
            this._updateSendBtn();
        } else {
            this.sendBtn.className = 'ws-send-btn';
            this.sendBtn.innerHTML = '<i class="bi bi-arrow-up"></i>';
            if (this.stopBtn) this.stopBtn.classList.remove('visible');
            this.textarea.disabled = false;
            this.textarea.focus();
            this._updateSendBtn();
        }
    }

    focus() {
        if (this.textarea) {
            this.textarea.focus();
        }
    }

    _submit() {
        const text = this.textarea.value.trim();
        const atts = [...this.attachments];
        if (!text && !atts.length) return;

        if (this.isProcessing) {
            this.workspace.queueFollowUpMessage(text, atts);
        } else {
            this.workspace.sendMessage(text, atts);
        }

        this.textarea.value = '';
        this.textarea.style.height = 'auto';
        this.attachments = [];
        this._renderPreviews();
        this._updateSendBtn();
    }

    renderPendingFollowUps(items = []) {
        if (!this.followUpArea) return;
        this.followUpArea.innerHTML = '';
        const actionableItems = items.filter(item => item.status === 'pending' || item.status === 'failed');
        if (!this.isProcessing && actionableItems.length > 0) {
            const actions = document.createElement('div');
            actions.className = 'ws-followup-actions';
            actions.innerHTML = `
                <button class="btn btn-sm btn-outline-secondary">Clear</button>
                <button class="btn btn-sm btn-primary"><i class="bi bi-send-fill me-1"></i> Submit queued</button>
            `;
            const [clearBtn, submitBtn] = actions.querySelectorAll('button');
            clearBtn.addEventListener('click', () => this.workspace.clearPendingFollowUps());
            submitBtn.addEventListener('click', () => this.workspace.submitPendingFollowUps());
            this.followUpArea.appendChild(actions);
        }
        for (const item of items) {
            const pill = document.createElement('div');
            pill.className = 'ws-followup-pill';
            const label = item.status === 'submitting' ? 'Submitting' : item.status === 'failed' ? 'Failed' : 'Queued';
            pill.innerHTML = `
                <i class="bi bi-chat-left-text text-secondary"></i>
                <span class="badge text-bg-secondary">${label}</span>
                <span class="followup-text"></span>
                <button class="remove-followup" title="Remove queued message"><i class="bi bi-x"></i></button>
            `;
            pill.querySelector('.followup-text').textContent = item.content || `${item.attachments?.length || 0} attachment(s)`;
            const removeBtn = pill.querySelector('.remove-followup');
            removeBtn.disabled = item.status === 'submitting';
            removeBtn.addEventListener('click', () => this.workspace.revokeFollowUpMessage(item.id));
            this.followUpArea.appendChild(pill);
        }
    }

    updateCapabilities() {
        if (!this.container) return;

        const supportsVoice = this.workspace.isVoiceSupported();
        const supportsUpload = this.workspace.isFileUploadSupported();

        if (this.voiceBtn) {
            this.voiceBtn.style.display = supportsVoice ? 'flex' : 'none';
        }

        if (this.attachBtn) {
            this.attachBtn.style.display = supportsUpload ? 'flex' : 'none';
        }
    }

    async attachDroppedFiles(files) {
        const supportsUpload = this.workspace.isFileUploadSupported();
        if (!supportsUpload) {
            this.appClient.notifier.error('File uploads are not supported in this workspace.');
            return;
        }

        if (!this.fileUploadComponent) {
            this.fileUploadComponent = await this.createManagedComponent('FileUpload', this.container, {
                instanceId: `${this.config.instanceId}-uploader`,
                hidden: true,
                maxFiles: 20,
                maxFileSize: 100 * 1024 * 1024, // 100MB
                getUploadKey: async () => {
                    if (!(await this.workspace.ensureThread())) throw new Error('A conversation is required for uploads.');
                    return this.appClient.rpcClient.call('AiRpc.getAttachmentUploadKey', {
                        agentName: this.workspace.currentAgentName,
                        threadId: this.workspace.currentThreadId
                    }, { projectId: this.workspace.currentProjectId });
                }
            });

            this.fileUploadComponent.on('uploadStarted', () => {
                this.attachBtn.disabled = true;
                this.attachBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" style="width: 14px; height: 14px;"></span>';
            });

            this.fileUploadComponent.on('uploadFailed', (err) => {
                this.attachBtn.disabled = false;
                this.attachBtn.innerHTML = '<i class="bi bi-paperclip"></i>';
                this.appClient.notifier.error('Failed to upload file.');
                this.fileUploadComponent.clear();
            });

            this.fileUploadComponent.on('uploadCompleted', async () => {
                const uploadKey = this.fileUploadComponent.getValue();
                if (!uploadKey) {
                    this.attachBtn.disabled = false;
                    this.attachBtn.innerHTML = '<i class="bi bi-paperclip"></i>';
                    return;
                }
                try {
                    const res = await this.appClient.rpcClient.call('AiRpc.finalizeAttachments', {
                        agentName: this.workspace.currentAgentName,
                        uploadKey
                    }, { projectId: this.workspace.currentProjectId });

                    if (res.success && res.files) {
                        res.files.forEach(f => this.attachments.push({
                            fileId: f.filePath,
                            fileName: f.fileName || 'file',
                            mimeType: f.fileType || 'application/octet-stream',
                            s3Uri: f.s3Uri
                        }));
                        this._renderPreviews();
                        this._updateSendBtn();
                    }
                } catch (e) {
                    this.appClient.notifier.error('Failed to attach files.');
                } finally {
                    this.fileUploadComponent.clear();
                    this.attachBtn.disabled = false;
                    this.attachBtn.innerHTML = '<i class="bi bi-paperclip"></i>';
                }
            });
        }

        this.fileUploadComponent.addFiles(files);
    }
}
